import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTheme, useMediaQuery, Box } from '@material-ui/core';
import {
  SignupTitleSection,
  AccountTypeSelector,
  EmailInput,
  PasswordInput,
  InflowRouteSelector,
  TermsAndServiceAgreement,
  SubmitButton
} from './Components';
import {
  CapaQuotationUserBizType,
  CapaServiceType,
  RegisterCapaUserInput,
  RegisterQuotationUserInput
} from '@generated/graphql';
import { useSignupStyle } from './styles';
import ProfileUpdateForm from '@components/Form/ProfileUpdateForm/ProfileUpdateForm';
import PostCodeDialog from '@components/Dialog/Postcode/PostCodeDialog';
import { CustomizedTypography } from '@components/Customized';
import { useParams } from 'react-router-dom';

interface TermsAndServiceState {
  serviceAgree: boolean;
  privacyAgree: boolean;
  paymentAgree: boolean;
  promotionAgree: boolean;
}
interface Props {
  onClickAuthButton: (
    email: string,
    service: CapaServiceType,
    callback: () => void
  ) => void;
  onSubmit: (form: RegisterQuotationUserInput & RegisterCapaUserInput) => void;
  bizType: CapaQuotationUserBizType;
  setBizType: (bizType: CapaQuotationUserBizType) => void;
  setAddress: React.Dispatch<
    React.SetStateAction<{
      full: string;
      short: string;
    }>
  >;
  agreeState: TermsAndServiceState;
  setAgreeState: React.Dispatch<React.SetStateAction<TermsAndServiceState>>;
  accountType: CapaServiceType;
  setAccountType: (state: CapaServiceType) => void;
}

const SignUpPresenter: React.FC<Props> = ({
  onClickAuthButton,
  onSubmit,
  bizType,
  setBizType,
  setAddress,
  agreeState,
  setAgreeState,
  accountType,
  setAccountType
}: Props) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const [emailAuthState, setEmailAuthState] = useState<boolean>(false);
  const [openPostCodeDialog, setOpenPostcodeDialog] = useState<boolean>(false);
  const { capaFriendsId } = useParams<{ capaFriendsId: string }>();
  const classes = useSignupStyle();
  const checked =
    agreeState.serviceAgree &&
    agreeState.privacyAgree &&
    agreeState.promotionAgree &&
    agreeState.paymentAgree;
  const formProps = useFormContext();

  return (
    <Box display="flex" className={classes.wrapper}>
      <PostCodeDialog
        open={openPostCodeDialog}
        setAddress={setAddress}
        onClose={() => setOpenPostcodeDialog(false)}
      />
      <SignupTitleSection />
      <Box className={classes.container}>
        <form onSubmit={formProps.handleSubmit(onSubmit)}>
          <AccountTypeSelector
            setAccountType={setAccountType}
            accountType={accountType}
          />
          {accountType === CapaServiceType.Quotation && (
            <Box className={classes.accountFormTitle}>
              <CustomizedTypography
                variant="h4"
                fontWeight="700"
                color="textPrimary"
              >
                계정 정보
              </CustomizedTypography>
            </Box>
          )}
          <EmailInput
            onClickAuthButton={onClickAuthButton}
            emailAuthState={emailAuthState}
            setEmailAuthState={(value: boolean) => setEmailAuthState(value)}
          />
          <PasswordInput />
          {accountType === CapaServiceType.Quotation && (
            <Box className={classes.updateFormBox}>
              <Box className={classes.updateFormTitle}>
                <CustomizedTypography
                  variant="h4"
                  fontWeight="700"
                  color="textPrimary"
                >
                  매칭을 위한 필수정보
                </CustomizedTypography>
              </Box>
              <ProfileUpdateForm
                setOpenPostCodeDialog={() => setOpenPostcodeDialog(true)}
                bizType={bizType}
                setBizType={setBizType}
              />
            </Box>
          )}
          {!capaFriendsId && <InflowRouteSelector />}
          <TermsAndServiceAgreement
            setAgreeState={(state: TermsAndServiceState) =>
              setAgreeState({ ...agreeState, ...state })
            }
            agreeState={agreeState}
            checked={checked}
          />
          <SubmitButton
            disabled={
              !formProps.formState.isValid ||
              !emailAuthState ||
              !agreeState.privacyAgree ||
              !agreeState.serviceAgree ||
              !agreeState.paymentAgree
            }
            matches={matches}
          />
        </form>
      </Box>
    </Box>
  );
};

export default SignUpPresenter;
